/**
 * An inline message that belongs to a region of the page: a failed run, a
 * warning about the input, a note about the result. For a passing notice that
 * belongs to no region, use `toast` instead.
 *
 * Errors get `role="alert"` so they are announced when they appear.
 */

import { CircleAlert, CircleCheck, Info, TriangleAlert, X, type LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

import { cn, ICON_BUTTON } from './styles'

export type AlertTone = 'error' | 'warning' | 'success' | 'info'

const TONE: Record<AlertTone, { icon: LucideIcon; box: string; iconClass: string }> = {
    error: {
        icon: CircleAlert,
        box: 'border-danger/30 bg-danger-soft text-fg',
        iconClass: 'text-danger',
    },
    warning: {
        icon: TriangleAlert,
        box: 'border-warning/40 bg-warning-soft text-fg',
        iconClass: 'text-warning',
    },
    success: {
        icon: CircleCheck,
        box: 'border-success/30 bg-success-soft text-fg',
        iconClass: 'text-success',
    },
    info: {
        icon: Info,
        box: 'border-info/30 bg-info-soft text-fg',
        iconClass: 'text-info',
    },
}

interface AlertProps {
    tone?: AlertTone
    title?: ReactNode
    onDismiss?: () => void
    className?: string
    children?: ReactNode
}

export default function Alert({ tone = 'error', title, onDismiss, className, children }: AlertProps) {
    const style = TONE[tone]
    const Icon = style.icon
    return (
        <div
            role={tone === 'error' ? 'alert' : 'status'}
            className={cn('flex items-start gap-3 rounded-2xl border p-4 text-sm', style.box, className)}
        >
            <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconClass}`} aria-hidden="true" />
            <div className="min-w-0 flex-1 space-y-1 wrap-break-word">
                {title && <p className="font-semibold">{title}</p>}
                {children}
            </div>
            {onDismiss && (
                <button type="button" onClick={onDismiss} aria-label="Meldung schließen" className={cn(ICON_BUTTON, `-m-1 h-7 w-7`)}>
                    <X className="h-4 w-4" aria-hidden="true" />
                </button>
            )}
        </div>
    )
}
